import { assertUnreachable } from "../utils/Functions";
import { CaseEvent } from "./Case";
import { Identifiable } from "./Identifiable";
import { AccountNextStep, AccountType, MeterType, PersonDetails } from "./NotifierFormData";
import { ServiceProvider } from "./ServiceProvider";
import { Property } from "../pages/NotifierForm/AccountForm/PropertyFields";
import { MeterAccess } from "../pages/NotifierForm/AccountForm/UtilitiesAccountFields/MeterAccessField";

export enum AccountStates {
  Closed = "CLOSED",
  Open = "OPEN",
}

export enum AccountStatuses {
  Processing = "PROCESSING",//in progress
  Settled = "SETTLED",// completed
  Blocked = "BLOCKED",//blocked
  Cancelled = "CANCELLED",//cancelled
}

// TODO: [backend] accountNumber, nextSteps, lastMeterReading are coming back as null instead of undefined.
export interface Account extends Identifiable {
  readonly insertedAt: string;
  // readonly case: Identifiable;
  readonly initialLetterSentAt?: string;
  readonly serviceProvider: ServiceProvider;
  readonly state: AccountStates;
  readonly status: AccountStatuses;
  readonly propertyRemainsOccupied: boolean;
  readonly finalBalance?: number;
  readonly outcomeDetails?: string;
  readonly accountEvents?: ReadonlyArray<CaseEvent>;
  readonly accountNumber?: string | null;
  readonly lastMeterReading?: string | null;
  readonly nextSteps?: AccountNextStep | null; // this field is a string on the backend but we can make it an enum.
  readonly estimateSettledBy?: string;

  readonly holderNumber?: string;
  readonly sortCode?: string | null;
  readonly accountType?: AccountType | null;
  readonly accountTypeOther?: string;
  readonly needsTaxCertificate?: boolean;
  readonly jointClaim?: boolean;
  readonly property?: Property;
  readonly meterType?: MeterType;
  readonly lastMeterReadingDate?: string;
  readonly meterAccess?: MeterAccess;
  readonly responsibleRejectsArrears?: boolean;

  readonly responsible: PersonDetails;
  readonly requiresNotifierAttention: boolean;

  // payment information
  readonly useCreditInNextBill?: boolean;
  readonly nextBillDueMonth?: string;
  readonly paymentPlanInPlace?: boolean;
  readonly paymentAmount?: boolean;
  readonly paymentFrequency?: string;
  readonly nextPaymentDueOn?: string;
  readonly paymentMethod?: string;

  readonly additionalServicesAccount?: boolean;
  readonly additionalServicesDetails?: {
    type: string;
  };
}
export const readableAccountStatuses = (s?: AccountStatuses, fallback?: string): string => {
  switch (s) {
    case AccountStatuses.Blocked:
      return "";

    case AccountStatuses.Processing:
      return "processing";

    case AccountStatuses.Settled:
      return "Account Settld";

    default:
      return fallback || "unknown";
  }
  // assertUnreachable(s);
};

const oneMonth = 1000 * 60 * 60 * 24 * 30;

export const readableAccountStatus = (account: Account): string => {
  switch (account.status) {
    case AccountStatuses.Blocked:
      return "Processing account.";

    case AccountStatuses.Processing:
      if (account.estimateSettledBy) {
        return `Expecting completion by ${formatDate(account.estimateSettledBy)}`;
      }

      if (account.initialLetterSentAt) {
        return `Expecting completion by ${formatDate(
          new Date(Date.parse(account.initialLetterSentAt) + oneMonth).toISOString()
        )}`;
      }

      return "Processing account.";

    default:
      return "";
  }
};

const accountNextStepDescription = (account: Account) => {
  if (account.nextSteps && account.nextSteps === AccountNextStep.Cancel) {
    return "Account closed.";
  }
  if (account.nextSteps && account.nextSteps === AccountNextStep.UpdateAccountHolder) {
    return "Account reassigned.";
  }
  if (account.nextSteps && account.nextSteps === AccountNextStep.RequestAccountBalance) {
    return "Account balance requested.";
  }

  return "";
};

const accountBalanceDescription = (account: Account) => {
  if (account.finalBalance && account.finalBalance > 0) {
    return "Money will be reimbursed.";
  }

  if (account.finalBalance && account.finalBalance < 0) {
    return "Payment required.";
  }

  return "";
};

const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.substring(0, 10).split(/-/);

  return `${day} ${monthName(month)} ${year}`;
};

const monthName = (month: string) => {
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
    parseInt(month, 10) - 1
  ];
};

export const estimateCompletionDate = (accounts: Account[]) => {
  const unknownCompletionDate = accounts.some((account) => {
    return (
      account.status === AccountStatuses.Blocked ||
      (account.status === AccountStatuses.Processing &&
        !account.estimateSettledBy &&
        !account.initialLetterSentAt)
    );
  });

  if (unknownCompletionDate) {
    return null;
  }

  const completionDates = accounts
    .filter((account) => {
      return (
        account.status === AccountStatuses.Processing &&
        (account.estimateSettledBy || account.initialLetterSentAt)
      );
    })
    .map((account) => {
      if (account.estimateSettledBy) {
        return account.estimateSettledBy;
      }

      if (account.initialLetterSentAt) {
        new Date(Date.parse(account.initialLetterSentAt) + oneMonth).toISOString();
      }

      return new Date(Date.now() + oneMonth).toISOString();
    });

  if (completionDates.length === 0) {
    return null;
  }

  completionDates.sort();

  return `We aim to complete your case by ${formatDate(
    completionDates[completionDates.length - 1]
  )}.`;
};
